/**
 * Image compression utility for handling large photos from phones
 * Aggressively compresses images to fit in localStorage
 */

import { encodeWithinBudget, PHOTO_PUSH_BODY_BUDGET_BYTES } from './photoPayloadBudget';

/**
 * Compress an image file to fit in localStorage
 * @param {File} file - The image file to compress
 * @param {number} maxSizeKB - Maximum size in KB (default 30KB for localStorage - more aggressive)
 * @param {Object} [opts] - Optional overrides.
 * @param {number} [opts.maxDimension] - Long-edge cap in px. When supplied it
 *   REPLACES the default 200px cap AND disables the >2MB down-shrink and the
 *   nuclear 100px fallback, so a high-resolution path (e.g. the FT-436 fleet-card
 *   PROFILE image, ~1280–1920px) is preserved. Existing callers pass no opts and
 *   keep the untouched 200px behaviour (crew photos / record evidence rely on it).
 * @param {number} [opts.startQuality] - Initial JPEG quality (default 0.6, or 0.4
 *   for >2MB inputs on the legacy path).
 * @returns {Promise<string>} - Base64 compressed image
 *
 * FT-577 WARNING: the high-res (opts.maxDimension) path has NO hard size
 * enforcement — it returns an over-budget image after 10 attempts. Do NOT use
 * it for anything that is uploaded via /sync/push; use
 * compressImageWithinBudget instead (the vessel profile image now does).
 */
export const compressImage = (file, maxSizeKB = 30, opts = {}) => {
  const { maxDimension, startQuality } = opts;
  const highRes = typeof maxDimension === 'number' && maxDimension > 0;
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        // Start with smaller dimensions for better compression
        let maxWidth = highRes ? maxDimension : 200;
        let maxHeight = highRes ? maxDimension : 200;
        let quality = typeof startQuality === 'number' ? startQuality : 0.6;

        // For very large images (>2MB), start with even smaller dimensions.
        // SKIPPED on the high-res path — a profile image must NOT be shrunk to
        // 150px just because the source file is big; the resolution is the point.
        if (!highRes && file.size > 2000000) {
          maxWidth = 150;
          maxHeight = 150;
          quality = 0.4;
        }
        
        let compressedImage = '';
        let attempts = 0;
        const maxAttempts = 10;
        
        // Keep trying with smaller dimensions and quality until we get under the limit
        while (attempts < maxAttempts) {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Calculate dimensions maintaining aspect ratio
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Use better image smoothing
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);
          
          // Try JPEG compression
          compressedImage = canvas.toDataURL('image/jpeg', quality);
          
          // Check if size is acceptable
          const sizeInKB = (compressedImage.length * 0.75) / 1024; // Approximate size
          
          if (sizeInKB <= maxSizeKB) {
            resolve(compressedImage);
            return;
          }

          // Reduce quality for next attempt. On the HIGH-RES (profile) path we
          // hold the resolution and only trade quality (down to 0.5) so the
          // fleet-card image stays sharp; on the legacy path we also shrink
          // dimensions as before.
          if (!highRes) {
            maxWidth = Math.round(maxWidth * 0.8);
            maxHeight = Math.round(maxHeight * 0.8);
          }
          quality = Math.max(highRes ? 0.5 : 0.3, quality - 0.1);
          attempts++;
        }

        // High-res path: never go nuclear — return the last (full-resolution)
        // attempt. A profile image slightly over budget is far better than a
        // 100px pixelated block. Legacy path keeps the nuclear fallback.
        if (highRes) {
          resolve(compressedImage);
          return;
        }

        // If we still can't get it small enough, go nuclear
        const tinyCanvas = document.createElement('canvas');
        const tinyCtx = tinyCanvas.getContext('2d');
        tinyCanvas.width = 100;
        tinyCanvas.height = 100;
        tinyCtx.drawImage(img, 0, 0, 100, 100);
        resolve(tinyCanvas.toDataURL('image/jpeg', 0.5));
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/** FT-577: exact decoded byte length of a base64 data-URL (no decoding needed). */
const dataUrlByteLength = (dataUrl) => {
  const comma = dataUrl.indexOf(',');
  const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  const padding = b64.endsWith('==') ? 2 : (b64.endsWith('=') ? 1 : 0);
  return Math.floor((b64.length * 3) / 4) - padding;
};

/** FT-577: center-cropped square JPEG thumbnail (same crop + quality as getThumbnail). */
const drawSquareThumbDataUrl = (img, size) => {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  let sourceX = 0;
  let sourceY = 0;
  let sourceWidth = img.width;
  let sourceHeight = img.height;
  if (img.width > img.height) {
    sourceX = (img.width - img.height) / 2;
    sourceWidth = img.height;
  } else {
    sourceY = (img.height - img.width) / 2;
    sourceHeight = img.width;
  }
  ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, size, size);
  return canvas.toDataURL('image/jpeg', 0.7);
};

/**
 * FT-577 (2026-11-01): compress an image AND its square thumbnail so the REAL
 * `/sync/push` request body fits a hard budget. Replaces the vessel-profile use
 * of compressImage's high-res path, which only lowered quality (never size)
 * toward a SOFT target and, after 10 attempts, saved the oversized result
 * anyway — that photo would then silently fail to upload.
 *
 * Decodes the source ONCE; the thumbnail is made first (from the source) so its
 * real size is part of every measurement. The full image then goes through the
 * shared enforcement loop (utils/photoPayloadBudget.encodeWithinBudget):
 *   1. at full resolution, trade quality down toward `softTargetKB` (the old
 *      profile behaviour — keeps the card sharp);
 *   2. if the real body is still over `budgetBytes`, reduce BOTH quality and
 *      dimensions until it fits;
 *   3. if it genuinely can't fit, REJECT with PhotoTooLargeError (plain-English
 *      message the caller must show) — never resolve with an oversized image.
 *
 * @param {File|Blob} file
 * @param {{
 *   maxDimension: number,           // long-edge cap for the full image
 *   startQuality?: number,          // default 0.8
 *   softTargetKB?: number,          // optional soft size target for the full image
 *   thumbSize?: number,             // square thumbnail px (default 512)
 *   budgetBytes?: number,           // hard request-body budget (default PHOTO_PUSH_BODY_BUDGET_BYTES)
 *   measure: (full: { bytes: number, mime: string, width: number, height: number },
 *             thumb: { bytes: number, mime: string }) => number,  // exact body bytes
 * }} opts
 * @returns {Promise<{ fullDataUrl: string, thumbDataUrl: string, width: number, height: number, quality: number, bodyBytes: number }>}
 */
export const compressImageWithinBudget = (file, opts = {}) => {
  const {
    maxDimension,
    startQuality = 0.8,
    softTargetKB,
    thumbSize = 512,
    budgetBytes = PHOTO_PUSH_BODY_BUDGET_BYTES,
    measure,
  } = opts;
  if (!(maxDimension > 0)) return Promise.reject(new Error('[compressImageWithinBudget] maxDimension is required'));
  if (typeof measure !== 'function') return Promise.reject(new Error('[compressImageWithinBudget] measure() is required'));

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error('Failed to load image'));
      img.onload = () => {
        (async () => {
          // Thumbnail first — same crop/quality as getThumbnail(), from source.
          const thumbDataUrl = drawSquareThumbDataUrl(img, thumbSize);
          const thumb = { bytes: dataUrlByteLength(thumbDataUrl), mime: 'image/jpeg' };

          const result = await encodeWithinBudget({
            srcWidth: img.width,
            srcHeight: img.height,
            startLongEdge: maxDimension,
            startQuality,
            budgetBytes,
            ...(softTargetKB ? { softTargetBytes: softTargetKB * 1024 } : {}),
            encode: (w, h, q) => {
              const canvas = document.createElement('canvas');
              canvas.width = w;
              canvas.height = h;
              const ctx = canvas.getContext('2d');
              ctx.imageSmoothingEnabled = true;
              ctx.imageSmoothingQuality = 'high';
              ctx.drawImage(img, 0, 0, w, h);
              const dataUrl = canvas.toDataURL('image/jpeg', q);
              return { dataUrl, size: dataUrlByteLength(dataUrl) };
            },
            measure: (enc, w, h) => measure({ bytes: enc.size, mime: 'image/jpeg', width: w, height: h }, thumb),
          });
          resolve({
            fullDataUrl: result.encoded.dataUrl,
            thumbDataUrl,
            width: result.width,
            height: result.height,
            quality: result.quality,
            bodyBytes: result.bodyBytes,
          });
        })().catch(reject);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Get a thumbnail version of an image
 * @param {string} base64Image - Base64 encoded image
 * @param {number} size - Thumbnail size (default 64px)
 * @returns {Promise<string>} - Base64 thumbnail
 */
export const getThumbnail = (base64Image, size = 64) => {
  return new Promise((resolve) => {
    const img = new Image();
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Square thumbnail
      canvas.width = size;
      canvas.height = size;
      
      // Calculate crop for square aspect
      let sourceX = 0;
      let sourceY = 0;
      let sourceWidth = img.width;
      let sourceHeight = img.height;
      
      if (img.width > img.height) {
        sourceX = (img.width - img.height) / 2;
        sourceWidth = img.height;
      } else {
        sourceY = (img.height - img.width) / 2;
        sourceHeight = img.width;
      }
      
      ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, size, size);
      resolve(canvas.toDataURL('image/jpeg', 0.7));
    };
    
    img.onerror = () => resolve(base64Image); // Return original if thumbnail fails
    img.src = base64Image;
  });
};

/**
 * FT-129: Compress a signature canvas to reduce storage size
 * Resizes to max 300x100 and keeps PNG (for transparency support)
 * @param {HTMLCanvasElement|Object} canvasOrRef - Canvas element or ref with toDataURL method
 * @returns {string} - Compressed base64 PNG signature
 */
export const compressSignature = (canvasOrRef) => {
  // Get the source canvas - handle both direct canvas and refs
  const sourceCanvas = canvasOrRef?.current || canvasOrRef;
  if (!sourceCanvas) return null;

  // Get the original data URL (works with react-signature-canvas refs too)
  const originalDataUrl = typeof sourceCanvas.toDataURL === 'function'
    ? sourceCanvas.toDataURL()
    : null;
  if (!originalDataUrl) return null;

  // Load into an image to resize
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      // Target max dimensions: 300x100 (maintains 3:1 signature aspect ratio)
      const maxWidth = 300;
      const maxHeight = 100;

      let width = img.width;
      let height = img.height;

      // Scale down proportionally if needed
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }
      if (height > maxHeight) {
        width = Math.round((width * maxHeight) / height);
        height = maxHeight;
      }

      // Create resized canvas
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);

      // Return as PNG to preserve transparency
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(originalDataUrl); // Fallback to original if resize fails
    img.src = originalDataUrl;
  });
};

/**
 * FT-129: Synchronous version for simple canvas refs (react-signature-canvas)
 * Uses white background + JPEG for maximum compression
 * @param {Object} sigCanvasRef - react-signature-canvas ref
 * @returns {string} - Compressed base64 signature
 */
export const compressSignatureSync = (sigCanvasRef) => {
  if (!sigCanvasRef?.current) return null;

  // Get the canvas element from react-signature-canvas
  const canvas = sigCanvasRef.current.getCanvas?.() || sigCanvasRef.current;
  if (!canvas) return sigCanvasRef.current.toDataURL?.() || null;

  // Target max dimensions
  const maxWidth = 300;
  const maxHeight = 100;

  let width = canvas.width;
  let height = canvas.height;

  // Scale down proportionally if needed
  if (width > maxWidth) {
    height = Math.round((height * maxWidth) / width);
    width = maxWidth;
  }
  if (height > maxHeight) {
    width = Math.round((width * maxHeight) / height);
    height = maxHeight;
  }

  // Create resized canvas with white background
  const resizedCanvas = document.createElement('canvas');
  resizedCanvas.width = width;
  resizedCanvas.height = height;

  const ctx = resizedCanvas.getContext('2d');
  // Fill with white background first (fixes JPEG transparency issue)
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, width, height);
  // Draw signature on top
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(canvas, 0, 0, width, height);

  // Return as JPEG with 60% quality (white background, no transparency issues)
  return resizedCanvas.toDataURL('image/jpeg', 0.6);
};

/**
 * Clean up old equipment images from localStorage
 * @param {string} currentJobId - The current job ID to preserve
 */
export const cleanupOldImages = (currentJobId) => {
  try {
    const keys = Object.keys(localStorage);
    const imageKeys = keys.filter(key => 
      key.startsWith('equipment_image_') && 
      !key.includes(currentJobId)
    );
    
    // Keep only the 10 most recent images (more aggressive cleanup)
    if (imageKeys.length > 10) {
      const timestamps = imageKeys.map(key => {
        const timestampKey = `${key}_timestamp`;
        return {
          key,
          time: parseInt(localStorage.getItem(timestampKey) || '0')
        };
      });
      
      timestamps.sort((a, b) => b.time - a.time);
      
      // Remove all but the 10 most recent
      const toRemove = timestamps.slice(10);
      toRemove.forEach(item => {
        localStorage.removeItem(item.key);
        localStorage.removeItem(`${item.key}_timestamp`);
        localStorage.removeItem(`${item.key}_thumbnail`);
      });
    }
  } catch (e) {
    // Error cleaning up images - fail silently
  }
};